import React, { useState, useEffect } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { ArrowLeft, Wallet, AlertCircle, Check } from "lucide-react";
import api from "../lib/api";

const VALIDATORS = {
  Bitcoin: /^(bc1[a-z0-9]{20,80}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/,
  Ethereum: /^0x[a-fA-F0-9]{40}$/,
  "Ethereum (ERC-20)": /^0x[a-fA-F0-9]{40}$/,
  Solana: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
  "Tron (TRC-20)": /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
  Polygon: /^0x[a-fA-F0-9]{40}$/,
  Base: /^0x[a-fA-F0-9]{40}$/,
  Arbitrum: /^0x[a-fA-F0-9]{40}$/,
  Optimism: /^0x[a-fA-F0-9]{40}$/,
  "Avalanche C-Chain": /^0x[a-fA-F0-9]{40}$/,
  "BNB Smart Chain": /^0x[a-fA-F0-9]{40}$/,
  "XRP Ledger": /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/,
  Cardano: /^(addr1|stake1)[a-z0-9]{20,}$/,
  Dogecoin: /^D[5-9A-HJ-NP-U][1-9A-HJ-NP-Za-km-z]{32,33}$/,
  Polkadot: /^[1-9A-HJ-NP-Za-km-z]{46,48}$/,
};

export default function WalletScreen({ quote, payment, onBack, onContinue, lockedAddress }) {
  const [address, setAddress] = useState(lockedAddress || "");
  const [book, setBook] = useState([]);

  useEffect(() => { api.get("/wallets").then((r) => setBook(r.data.wallets || [])).catch(()=>{}); }, []);

  const validator = VALIDATORS[quote.network];
  const isValid = !!address && (!validator || validator.test(address));
  const locked = !!lockedAddress;

  return (
    <div className="space-y-5" data-testid="wallet-screen">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-primary transition-colors" data-testid="wallet-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div>
        <h2 className="font-display text-2xl font-bold text-primary">Where should we send your {quote.asset}?</h2>
        <p className="text-sm text-slate-500 mt-1">
          Paste your own {quote.network} wallet address. We never custody your crypto.
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {quote.asset} · {quote.network} address
        </Label>
        <div className="relative">
          <Wallet className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
          <Input
            value={address} onChange={(e) => setAddress(e.target.value)}
            placeholder={`Paste your ${quote.network} address`}
            disabled={locked}
            className="rounded-2xl pl-11 pr-10 h-12 font-mono text-sm bg-slate-50 border-slate-200 disabled:opacity-90 disabled:cursor-not-allowed"
            data-testid="wallet-address-input"
          />
          {address && (
            <div className="absolute right-3 top-3.5">
              {isValid
                ? <Check className="w-5 h-5 text-accent" data-testid="wallet-valid-icon" />
                : <AlertCircle className="w-5 h-5 text-destructive" data-testid="wallet-invalid-icon" />}
            </div>
          )}
        </div>
        {address && !isValid && (
          <div className="text-xs text-destructive font-semibold flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> Invalid {quote.network} address format
          </div>
        )}
        <div className="text-[11px] text-slate-500">
          {locked
            ? "This address is pre-set by the partner site and can't be changed."
            : "Double-check this address — crypto sent to wrong addresses can't be recovered."}
        </div>
      </div>

      {book.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Saved addresses</Label>
          <div className="space-y-1.5">
            {book.filter((w) => w.asset === quote.asset && w.network === quote.network).slice(0, 3).map((w) => (
              <button
                key={w.id} onClick={() => setAddress(w.address)}
                className="w-full text-left bg-slate-50 hover:bg-slate-100 rounded-xl p-2.5 transition-colors"
                data-testid={`saved-wallet-${w.id}`}
              >
                <div className="font-display font-semibold text-sm text-primary">{w.label}</div>
                <div className="text-xs text-slate-500 font-mono truncate">{w.address}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <Button
        onClick={() => onContinue(address)} disabled={!isValid}
        className="w-full bg-accent hover:bg-accent/90 text-white rounded-xl h-12 font-display font-semibold"
        data-testid="wallet-continue-btn"
      >
        Continue
      </Button>
    </div>
  );
}
